import {
  DEEPGRAM_EN_ONLY_MODEL,
  type STTProvider,
  type TTSProvider,
} from '@/shared/types/voiceProviders';

export type LanguageFieldConfig = {
  /** Dot-notation path to the language field in the form (prefixed with 'config.') */
  fieldPath: string;
  /** Transform a BrainLanguage code to the provider's expected format */
  toProvider: (lang: string) => string | string[];
  /** Transform the provider's stored value back to a base language code */
  fromProvider: (val: unknown) => string;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract base ISO 639-1 code: 'pt-br' → 'pt', 'nl-be' → 'nl', 'en' → 'en' */
const toBaseIso = (lang: string): string => lang.split('-')[0];

/** Extract a string value, defaulting to empty string */
const fromString = (val: unknown): string => (typeof val === 'string' ? val : '');

// ---------------------------------------------------------------------------
// Provider-specific toProvider transforms
//
// Each function maps a BrainLanguage code to the code accepted by the
// provider's backend Joi validation (see orchestrator languages.ts).
// Falls back to 'en' when the language has no match in the provider's list.
// ---------------------------------------------------------------------------

// Keep in sync with orchestrator src/modules/integrations/languages.ts
// These are BrainLanguage codes absent from the provider's supported language array.

// Deepgram uses IETF-cased regional variants (pt-BR, nl-BE)
const DEEPGRAM_REGIONAL_MAP: Record<string, string> = { 'pt-br': 'pt-BR', 'nl-be': 'nl-BE' };

const toDeepgram = (lang: string): string => DEEPGRAM_REGIONAL_MAP[lang] ?? toBaseIso(lang);

// Languages supported by the flux-general-multi model.
// Keep in sync with orchestrator DEEPGRAM_FLUX_MULTI_LANGUAGES (languages.ts).
const DEEPGRAM_FLUX_MULTI_LANGUAGES = new Set([
  'en',
  'es',
  'fr',
  'de',
  'hi',
  'ru',
  'pt',
  'ja',
  'it',
  'nl',
]);

/**
 * Deepgram's language is gated by its model, and an unsupported language fails
 * server validation. flux-general-en is English-only; flux-general-multi only
 * accepts DEEPGRAM_FLUX_MULTI_LANGUAGES. Fall back to 'en' when the model can't
 * do the language (and when the model is unset, which the server treats as
 * English-only).
 */
export const resolveDeepgramLanguage = (lang: string, model: string | undefined): string => {
  if (!model || model === DEEPGRAM_EN_ONLY_MODEL) {
    return 'en';
  }
  const mapped = toDeepgram(lang);
  if (model === 'flux-general-multi') {
    return DEEPGRAM_FLUX_MULTI_LANGUAGES.has(toBaseIso(mapped)) ? mapped : 'en';
  }
  return mapped;
};

// ElevenLabs STT (scribe_v2_realtime) uses 'fil' for Tagalog
const ELEVENLABS_STT_UNSUPPORTED = new Set(['sq']);

const toElevenLabsStt = (lang: string): string => {
  const base = toBaseIso(lang);
  if (base === 'tl') {
    return 'fil';
  }
  return ELEVENLABS_STT_UNSUPPORTED.has(base) ? 'en' : base;
};

// Soniox supports all BrainLanguage base codes
const toSoniox = (lang: string): string[] => [toBaseIso(lang)];

// ElevenLabs TTS: uses 'fil' for Tagalog; sq/sr/cy/th not in any model's list
const ELEVENLABS_TTS_UNSUPPORTED = new Set(['sq', 'sr', 'cy', 'th']);

const toElevenLabsTts = (lang: string): string => {
  const base = toBaseIso(lang);
  if (base === 'tl') {
    return 'fil';
  }
  return ELEVENLABS_TTS_UNSUPPORTED.has(base) ? 'en' : base;
};

// Cartesia TTS: sq/sr/cy not supported
const CARTESIA_TTS_UNSUPPORTED = new Set(['sq', 'sr', 'cy']);

const toCartesiaTts = (lang: string): string => {
  const base = toBaseIso(lang);
  return CARTESIA_TTS_UNSUPPORTED.has(base) ? 'en' : base;
};

// MiniMax TTS: voices are multilingual, language is a hint (language_boost).
// Keep in sync with orchestrator MINIMAX_TTS_LANGUAGES; unsupported → 'auto'.
const MINIMAX_TTS_SUPPORTED = new Set([
  'en',
  'zh',
  'ja',
  'ko',
  'es',
  'fr',
  'pt',
  'de',
  'it',
  'nl',
  'ru',
  'uk',
  'tr',
  'ar',
  'vi',
  'id',
  'th',
  'pl',
  'ro',
  'el',
  'cs',
  'fi',
  'hi',
]);

const toMiniMaxTts = (lang: string): string => {
  const base = toBaseIso(lang);
  return MINIMAX_TTS_SUPPORTED.has(base) ? base : 'auto';
};

// ---------------------------------------------------------------------------
// Provider-specific fromProvider transforms
//
// Reverse-map provider codes back to a base ISO code so the legacy
// resolution in schemas.ts can match it to a BrainLanguage value.
// ---------------------------------------------------------------------------

/** ElevenLabs stores 'fil' for Filipino/Tagalog — reverse to 'tl' */
const fromElevenLabs = (val: unknown): string => {
  const code = fromString(val);
  return code === 'fil' ? 'tl' : code;
};

/** Deepgram stores IETF-cased codes (pt-BR) — lowercase for BrainLanguage matching */
const fromDeepgram = (val: unknown): string => fromString(val).toLowerCase();

// ---------------------------------------------------------------------------
// Maps
// ---------------------------------------------------------------------------

export const STT_PROVIDER_LANGUAGE_MAP: Record<STTProvider, LanguageFieldConfig> = {
  deepgram: {
    fieldPath: 'config.stt_deepgram.language',
    toProvider: toDeepgram,
    fromProvider: fromDeepgram,
  },
  elevenlabs: {
    fieldPath: 'config.stt_elevenlabs.language_code',
    toProvider: toElevenLabsStt,
    fromProvider: fromElevenLabs,
  },
  soniox: {
    fieldPath: 'config.stt_soniox.language_hints',
    toProvider: toSoniox,
    fromProvider: (val) => (Array.isArray(val) ? (val[0] ?? '') : ''),
  },
};

export const TTS_PROVIDER_LANGUAGE_MAP: Record<TTSProvider, LanguageFieldConfig> = {
  elevenlabs: {
    fieldPath: 'config.tts_elevenlabs.language_code',
    toProvider: toElevenLabsTts,
    fromProvider: fromElevenLabs,
  },
  cartesia: {
    fieldPath: 'config.tts_cartesia.language_code',
    toProvider: toCartesiaTts,
    fromProvider: fromString,
  },
  minimax: {
    fieldPath: 'config.tts_minimax.language_boost',
    toProvider: toMiniMaxTts,
    fromProvider: fromString,
  },
};

// ---------------------------------------------------------------------------
// Language support check
// ---------------------------------------------------------------------------

/**
 * Returns true when a BrainLanguage code is supported by at least one STT
 * provider AND at least one TTS provider.
 *
 * For regional variants (e.g. 'pt-br', 'nl-be') the base language ('pt', 'nl')
 * is checked as a fallback — so pt-br is kept if pt has provider support.
 */
export const hasVoiceProviderSupport = (lang: string): boolean => {
  const base = toBaseIso(lang);
  const hasTts = !ELEVENLABS_TTS_UNSUPPORTED.has(base) || !CARTESIA_TTS_UNSUPPORTED.has(base);
  const hasStt = !ELEVENLABS_STT_UNSUPPORTED.has(base);
  // Soniox supports all languages, so hasStt is always true in practice,
  // but we keep the check explicit for correctness if providers change.
  return hasTts && hasStt;
};

/** All provider language configs combined — used by useProviderLanguage to write all at once */
export const ALL_PROVIDER_LANGUAGE_CONFIGS: LanguageFieldConfig[] = [
  STT_PROVIDER_LANGUAGE_MAP.deepgram,
  STT_PROVIDER_LANGUAGE_MAP.elevenlabs,
  STT_PROVIDER_LANGUAGE_MAP.soniox,
  TTS_PROVIDER_LANGUAGE_MAP.elevenlabs,
  TTS_PROVIDER_LANGUAGE_MAP.cartesia,
  TTS_PROVIDER_LANGUAGE_MAP.minimax,
];
